// src/native/bootstrap.js
// Native shell wiring. No-ops on web (every call is guarded by isNative()),
// so it's safe to invoke unconditionally from index.js. Handles:
//   - status bar colour (brand purple, light text)
//   - hiding the splash screen once React has mounted
//   - Android hardware back button → SPA history back, or exit at root
//   - keyboard resize behaviour
//   - native-aware "exit confirmation" at the app root
//
// Everything is dynamically imported so the web bundle doesn't eagerly load
// native plugin code paths it never uses.

import { isNative, getPlatform } from '../config/runtime';

const BRAND = '#3D1E6D';

let initialized = false;

export async function initNativeShell() {
	if (initialized) return;
	initialized = true;
	if (!isNative()) return;

	const platform = getPlatform();

	// ---- Status bar -------------------------------------------------------
	try {
		const { StatusBar, Style } = await import('@capacitor/status-bar');
		await StatusBar.setStyle({ style: Style.Light });
		if (platform === 'android') {
			await StatusBar.setBackgroundColor({ color: BRAND });
			await StatusBar.setOverlaysWebView({ overlay: false });
		}
	} catch (e) {
		// status bar plugin unavailable — non-fatal
	}

	// ---- Keyboard ---------------------------------------------------------
	try {
		const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard');
		await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
		// Hide the keyboard accessory bar on iOS for a cleaner look.
		if (platform === 'ios' && Keyboard.setAccessoryBarVisible) {
			await Keyboard.setAccessoryBarVisible({ isVisible: false });
		}
	} catch (e) {
		/* non-fatal */
	}

	// ---- Hardware back button (Android) ----------------------------------
	try {
		const { App } = await import('@capacitor/app');
		App.addListener('backButton', ({ canGoBack }) => {
			// At a top-level route there's nothing to go back to — confirm exit
			// rather than silently dropping the user out of the app.
			const atRoot = isRootRoute(window.location.pathname);
			if (!canGoBack || atRoot) {
				const ok = window.confirm('Leave Eventory?');
				if (ok) App.exitApp();
				return;
			}
			window.history.back();
		});
	} catch (e) {
		/* non-fatal */
	}
}

// Hide the launch splash once the React tree has painted. Called from the
// app shell after first meaningful render.
export async function hideSplash() {
	if (!isNative()) return;
	try {
		const { SplashScreen } = await import('@capacitor/splash-screen');
		await SplashScreen.hide();
	} catch (e) {
		/* non-fatal */
	}
}

// Top-level routes where "back" should exit the app rather than navigate.
function isRootRoute(pathname) {
	const roots = ['/', '/dashboard', '/login'];
	return roots.includes(pathname);
}
